import { useCallback, useRef, useState } from "react";
import createIdempotencyKey from "../utils/createIdempotencyKey";

/** What the guarded action is handed as its first argument. */
export interface IdempotencyContext {
  idempotencyKey: string;
}

type IdempotentAction<TArgs extends unknown[], TResponse> = (
  context: IdempotencyContext,
  ...args: TArgs
) => Promise<TResponse>;

/**
 * Whether the attempt is done with its key.
 *
 * Responses report success two ways in this codebase - as a method, which is what sendRequest gives
 * back, and as a plain boolean, as in openId's `{ isOk: boolean }` - so both are read. Calling a
 * boolean would throw, and reading a method as a value is always truthy, so neither shape can be
 * assumed. A response that cannot report a failure at all is taken at its word, so an action that
 * returns nothing is not left retrying under a key the backend has already answered.
 */
const hasSucceeded = (response: unknown): boolean => {
  if (response == null || typeof response !== "object") return true;

  const isOk = (response as { isOk?: unknown }).isOk;

  if (typeof isOk === "function") return (isOk as () => unknown).call(response) !== false;
  if (typeof isOk === "boolean") return isOk;

  return true;
};

/**
 * Wraps an action that writes something so that it cannot be started twice by accident, and hands it
 * the idempotency key of the attempt.
 *
 * The blocking is done with a ref, not with the returned isSubmitting flag: two clicks landing in the
 * same tick both run before React has re-rendered the button as disabled, so state is decided too
 * late to stop the second one. Disabling the button on isSubmitting is still worth doing - it is what
 * the user sees - but it is not what makes this safe.
 *
 * The key lives as long as the attempt does. It is kept when an attempt fails, so a retry sends the
 * same key and a request that reached the server while its answer did not reach us is not applied
 * twice; it is dropped once an attempt succeeds, so the next deliberate action is recognised as new
 * and adds to the order as the user expects.
 *
 * The action is called as `action({ idempotencyKey }, ...args)` - the context comes first so that its
 * position does not depend on how many arguments the caller passes on.
 */
const useIdempotentSubmit = <TArgs extends unknown[], TResponse>(
  action: IdempotentAction<TArgs, TResponse>,
): [(...args: TArgs) => Promise<TResponse | undefined>, boolean] => {
  const isSubmittingRef = useRef(false);
  const idempotencyKeyRef = useRef<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = useCallback(
    async (...args: TArgs): Promise<TResponse | undefined> => {
      if (isSubmittingRef.current) return undefined;

      isSubmittingRef.current = true;
      setIsSubmitting(true);

      if (!idempotencyKeyRef.current) idempotencyKeyRef.current = createIdempotencyKey();

      try {
        const response = await action({ idempotencyKey: idempotencyKeyRef.current }, ...args);

        if (hasSucceeded(response)) idempotencyKeyRef.current = null;

        return response;
      } finally {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }
    },
    [action],
  );

  return [submit, isSubmitting];
};

export default useIdempotentSubmit;
